import { useState, useEffect, useCallback, useRef } from "react";
import { useAuth } from "../contexts/AuthContext";
import { useNavigate, useSearchParams } from "react-router-dom";
import API from "../lib/api";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Badge } from "../components/ui/badge";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "../components/ui/tabs";
import AutoSuspendToggle from "../components/AutoSuspendToggle";
import { Select, SelectContent, SelectGroup, SelectItem, SelectLabel, SelectTrigger, SelectValue } from "../components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "../components/ui/dialog";
import { Textarea } from "../components/ui/textarea";
import { UkAreasPicker } from "../components/UkAreasPicker";
import { SupplierCoverageMap } from "../components/SupplierCoverageMap";
import { ServicesPicker } from "../components/ServicesPicker";
import { UK_REGIONS, ALL_UK_COUNTIES, ALL_UK_AREAS } from "../lib/ukAreas";
import { Plus, MagnifyingGlass, PencilSimple, ShieldCheck, ShieldWarning, Trash, CheckCircle, Upload, DownloadSimple, ArrowsClockwise, FileArrowUp, ClockCounterClockwise, EnvelopeSimple, LinkSimple } from "@phosphor-icons/react";
import { toast } from "sonner";
import AddressAutocomplete from "../components/AddressAutocomplete";
import SlaShield from "../components/SlaShield";
import SupplierBadgeRibbon from "../components/SupplierBadgeRibbon";
import SubcontractorBadge from "../components/SubcontractorBadge";
import { fmtDate } from "../lib/dateUtil";

import DateInput from "../components/DateInput";
const DOC_TYPES = [
  "insurance", "public_liability", "employers_liability", "professional_indemnity",
  "gas_safe", "niceic", "napit", "chas", "safe_contractor", "constructionline",
  "dbs_check", "cscs_card", "asbestos_awareness", "first_aid", "fire_safety",
  "method_statement", "risk_assessment", "waste_carrier_license", "other"
];

function ComplianceStatusBadge({ status }) {
  const cfg = {
    valid: { label: "Valid", cls: "bg-emerald-100 text-emerald-700 dark:bg-emerald-900/40 dark:text-emerald-400" },
    expiring_soon: { label: "Expiring Soon", cls: "bg-amber-100 text-amber-700 dark:bg-amber-900/40 dark:text-amber-400" },
    expired: { label: "Expired", cls: "bg-red-100 text-red-700 dark:bg-red-900/40 dark:text-red-400" },
    no_expiry: { label: "No Expiry", cls: "bg-slate-100 text-slate-600 dark:bg-slate-800 dark:text-slate-400" },
    unknown: { label: "Unknown", cls: "bg-slate-100 text-slate-600 dark:bg-slate-800 dark:text-slate-400" },
  };
  const c = cfg[status] || cfg.unknown;
  return <span className={`inline-flex items-center px-2 py-0.5 rounded text-[10px] font-semibold ${c.cls}`} data-testid={`compliance-status-${status}`}>{c.label}</span>;
}

export default function VendorsPage() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const isVendor = user?.role === "vendor";
  const isAdmin = ["super_admin", "admin", "project_manager"].includes(user?.role);

  const [vendors, setVendors] = useState([]);
  const [vendorScores, setVendorScores] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState("");
  const [sortBy, setSortBy] = useState("name"); // name | sla | response
  const [activeTab, setActiveTab] = useState("directory");
  const [refreshingSla, setRefreshingSla] = useState(false);
  const [tradeFilter, setTradeFilter] = useState("");
  const [areaFilter, setAreaFilter] = useState("");
  const [filterOptions, setFilterOptions] = useState({ trades: [], areas: [] });
  const [showCreate, setShowCreate] = useState(false);
  const [showEdit, setShowEdit] = useState(false);
  const [editVendor, setEditVendor] = useState(null);
  const [form, setForm] = useState({ company_name: "", contact_name: "", email: "", phone: "", address: "", street_address: "", town_city: "", county: "", postcode: "", latitude: null, longitude: null, trade: "", coverage_area: "", accreditations: "", sectors: [], areas_covered: [], company_registration_number: "", vat_number: "", website: "" });

  // Compliance state
  const [complianceSummary, setComplianceSummary] = useState(null);
  const [selectedVendorId, setSelectedVendorId] = useState(null);
  const [complianceDocs, setComplianceDocs] = useState([]);
  const [showAddDoc, setShowAddDoc] = useState(false);
  const [docForm, setDocForm] = useState({ doc_type: "insurance", document_number: "", description: "", issue_date: "", expiry_date: "", notes: "" });
  const [loadingDocs, setLoadingDocs] = useState(false);
  // Bulk import
  const [showBulkImport, setShowBulkImport] = useState(false);
  const [importFile, setImportFile] = useState(null);
  const [importing, setImporting] = useState(false);
  const [importResult, setImportResult] = useState(null);
  const [showBulkInvite, setShowBulkInvite] = useState(false);
  const [showRefreshInvite, setShowRefreshInvite] = useState(false);
  const [refreshInviting, setRefreshInviting] = useState(false);
  const [refreshInviteResult, setRefreshInviteResult] = useState(null);
  const [refreshProgress, setRefreshProgress] = useState(null);

  useEffect(() => {
    if (!showRefreshInvite) return;
    API.get("/supplier-applications/refresh-progress").then(({ data }) => setRefreshProgress(data)).catch(() => {});
  }, [showRefreshInvite, refreshInviteResult]);
  const [bulkInviteOnlyMissing, setBulkInviteOnlyMissing] = useState(true);
  const [bulkInviting, setBulkInviting] = useState(false);
  const [bulkInviteResult, setBulkInviteResult] = useState(null);
  // Compliance reminders
  const [sendingReminders, setSendingReminders] = useState(false);

  // Vendor self-service state
  const [myVendorProfile, setMyVendorProfile] = useState(null);
  const [myDocs, setMyDocs] = useState([]);
  const [myDocsLoading, setMyDocsLoading] = useState(true);
  const [showUploadDoc, setShowUploadDoc] = useState(false);
  const [uploadForm, setUploadForm] = useState({ doc_type: "insurance", document_number: "", description: "", issue_date: "", expiry_date: "", notes: "" });
  const [uploading, setUploading] = useState(false);

  // Document versioning state
  const [showVersionHistory, setShowVersionHistory] = useState(false);
  const [versionDocId, setVersionDocId] = useState(null);
  const [versions, setVersions] = useState([]);
  const [loadingVersions, setLoadingVersions] = useState(false);
  const [showNewVersion, setShowNewVersion] = useState(false);
  const [versionForm, setVersionForm] = useState({ document_number: "", description: "", issue_date: "", expiry_date: "", notes: "" });
  const [uploadingVersion, setUploadingVersion] = useState(false);

  // File attachment state
  const [fileInput, setFileInput] = useState(null);
  const [uploadingFile, setUploadingFile] = useState(false);
  const [docFiles, setDocFiles] = useState([]);
  const [showFiles, setShowFiles] = useState(false);
  const [filesDocId, setFilesDocId] = useState(null);
  const [dragOver, setDragOver] = useState(false);
  const [multiUploading, setMultiUploading] = useState(false);
  const [uploadProgress, setUploadProgress] = useState([]);

  const fetchVendors = useCallback(async () => {
    if (isVendor) { setLoading(false); return; }
    try {
      const params = {};
      if (search) params.search = search;
      if (tradeFilter) params.trade = tradeFilter;
      if (areaFilter) params.area = areaFilter;
      const { data } = await API.get("/suppliers", { params });
      setVendors(data);
    } catch (err) { console.error(err); }
    finally { setLoading(false); }
  }, [search, tradeFilter, areaFilter, isVendor]);

  const fetchComplianceSummary = useCallback(async () => {
    if (isVendor) return;
    try {
      const { data } = await API.get("/compliance/summary");
      setComplianceSummary(data);
    } catch {}
  }, [isVendor]);

  const fetchMyCompliance = useCallback(async () => {
    if (!isVendor) { setMyDocsLoading(false); return; }
    try {
      const { data } = await API.get("/vendor-portal/my-compliance");
      setMyVendorProfile(data.vendor);
      setMyDocs(data.documents || []);
    } catch (err) { console.error(err); }
    finally { setMyDocsLoading(false); }
  }, [isVendor]);

  useEffect(() => {
    fetchVendors();
    if (isAdmin) {
      API.get("/reports/vendor-performance").then(({ data }) => setVendorScores(data)).catch(() => {});
      API.get("/suppliers/filter-options").then(({ data }) => setFilterOptions(data)).catch(() => {});
      fetchComplianceSummary();
    }
    if (isVendor) {
      fetchMyCompliance();
    }
  }, [fetchVendors, fetchComplianceSummary, fetchMyCompliance, isAdmin, isVendor]);

  // Phase 244 — fetch current-period badges to flag winners in the directory.
  const [vendorBadges, setVendorBadges] = useState({}); // {vendor_id: badge}
  useEffect(() => {
    if (!isAdmin) return;
    let cancel = false;
    (async () => {
      try {
        const { data } = await API.get("/suppliers/badges/leaderboard", { params: { limit: 12 } });
        if (cancel) return;
        const map = {};
        (data?.badges || []).forEach(b => {
          // keep only the most recent (badges are pre-sorted desc by period_start)
          if (!map[b.vendor_id]) map[b.vendor_id] = b;
        });
        setVendorBadges(map);
      } catch {}
    })();
    return () => { cancel = true; };
  }, [isAdmin]);

  // Auto-open create dialog when arriving with ?new=1
  const [searchParams, setSearchParams] = useSearchParams();
  useEffect(() => {
    if (isAdmin && searchParams.get("new") === "1") {
      setShowCreate(true);
      const next = new URLSearchParams(searchParams);
      next.delete("new");
      setSearchParams(next, { replace: true });
    }
  }, [searchParams, setSearchParams, isAdmin]);

  const fetchVendorCompliance = async (vendorId) => {
    setSelectedVendorId(vendorId);
    setLoadingDocs(true);
    try {
      const { data } = await API.get(`/suppliers/${vendorId}/compliance`);
      setComplianceDocs(data);
    } catch { setComplianceDocs([]); }
    finally { setLoadingDocs(false); }
  };

  const handleCreate = async (e) => {
    e.preventDefault();
    if (!(form.sectors || []).length) { toast.error("Select at least one service"); return; }
    if (!(form.areas_covered || []).length) { toast.error("Select at least one area covered"); return; }
    try {
      await API.post("/suppliers", { ...form, trade: form.sectors.join(", "), coverage_area: form.areas_covered.join(", ") });
      toast.success("Supplier added");
      setShowCreate(false);
      setForm({ company_name: "", contact_name: "", email: "", phone: "", address: "", street_address: "", town_city: "", county: "", postcode: "", latitude: null, longitude: null, trade: "", coverage_area: "", accreditations: "", sectors: [], areas_covered: [], company_registration_number: "", vat_number: "", website: "" });
      fetchVendors();
    } catch { toast.error("Failed to create supplier"); }
  };

  const handleEdit = async (e) => {
    e.preventDefault();
    try {
      const payload = { ...editVendor };
      if ((payload.areas_covered || []).length) payload.coverage_area = payload.areas_covered.join(", ");
      if ((payload.sectors || []).length) payload.trade = payload.sectors.join(", ");
      await API.put(`/suppliers/${editVendor.id}`, payload);
      toast.success("Supplier updated");
      setShowEdit(false);
      fetchVendors();
    } catch { toast.error("Failed to update supplier"); }
  };

  const handleDeleteVendor = async (vendor) => {
    if (!window.confirm(`Permanently delete "${vendor.company_name}"? This will remove the supplier, their compliance documents, and any linked user account. This cannot be undone.`)) return;
    try {
      await API.delete(`/suppliers/${vendor.id}`);
      toast.success("Supplier deleted permanently");
      fetchVendors();
    } catch { toast.error("Failed to delete supplier"); }
  };

  const handleInviteVendor = async (vendor) => {
    if (!vendor.email) { toast.error("Supplier has no email address. Please add one first."); return; }
    try {
      const { data } = await API.post(`/suppliers/${vendor.id}/invite`);
      toast.success(data.message);
      fetchVendors();
    } catch (err) { toast.error(err.response?.data?.detail || "Failed to send invite"); }
  };

  const handleCopySetupLink = async (vendor) => {
    if (vendor.user_id && vendor.invite_status === "accepted") {
      toast.error("This supplier has already set up their account.");
      return;
    }
    try {
      const { data } = await API.post(`/suppliers/${vendor.id}/setup-link`);
      try {
        await navigator.clipboard.writeText(data.url);
        toast.success("Setup link copied to clipboard");
      } catch {
        // Clipboard API blocked (e.g. insecure context) — show the URL so admin can copy manually
        window.prompt("Copy this setup link", data.url);
      }
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to generate link");
    }
  };

  const handleRefreshInvite = async (onlyOutstanding = false) => {
    setRefreshInviting(true);
    try {
      const { data } = await API.post("/supplier-applications/invite-refresh", onlyOutstanding ? { only_outstanding: true } : {});
      setRefreshInviteResult(data);
      toast.success(`${data.sent} ${onlyOutstanding ? "re-invite" : "profile refresh invite"}${data.sent === 1 ? "" : "s"} sent`);
    } catch (e) { toast.error(e?.response?.data?.detail || "Failed to send invites"); }
    finally { setRefreshInviting(false); }
  };

  const handleBulkInvite = async () => {
    setBulkInviting(true);
    setBulkInviteResult(null);
    try {
      const { data } = await API.post("/suppliers/bulk-invite", { only_missing_account: bulkInviteOnlyMissing });
      setBulkInviteResult(data);
      toast.success(data.message);
      fetchVendors();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to send bulk invites");
    } finally {
      setBulkInviting(false);
    }
  };

  const handleLinkUsers = async () => {
    setBulkInviting(true);
    try {
      const { data } = await API.post("/suppliers/link-users");
      toast.success(data.message);
      setBulkInviteResult({
        sent: 0,
        total_candidates: 0,
        skipped_accepted: 0,
        skipped_no_email: 0,
        failed: [],
        linked_msg: data.message,
        healed: (data.jobs_repointed || 0) + (data.emergency_jobs_repointed || 0) + (data.tasks_repointed || 0),
        link_details: data.details || [],
      });
      fetchVendors();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to link users");
    } finally {
      setBulkInviting(false);
    }
  };

  const [adminUploadFile, setAdminUploadFile] = useState(null);

  const handleAddComplianceDoc = async (e) => {
    e.preventDefault();
    try {
      const { data: doc } = await API.post(`/suppliers/${selectedVendorId}/compliance`, docForm);
      if (adminUploadFile && doc.id) {
        const formData = new FormData();
        formData.append("file", adminUploadFile);
        await API.post(`/compliance/${doc.id}/attach-file`, formData, { headers: { "Content-Type": "multipart/form-data" } });
      }
      toast.success("Compliance document added");
      setShowAddDoc(false);
      setDocForm({ doc_type: "insurance", document_number: "", description: "", issue_date: "", expiry_date: "", notes: "" });
      setAdminUploadFile(null);
      fetchVendorCompliance(selectedVendorId);
      fetchComplianceSummary();
    } catch { toast.error("Failed to add document"); }
  };

  const handleVerifyDoc = async (docId) => {
    try {
      await API.put(`/compliance/${docId}/verify`);
      toast.success("Document verified");
      fetchVendorCompliance(selectedVendorId);
    } catch { toast.error("Failed to verify"); }
  };

  const handleDeleteDoc = async (docId) => {
    try {
      await API.delete(`/compliance/${docId}`);
      toast.success("Document deleted");
      fetchVendorCompliance(selectedVendorId);
      fetchComplianceSummary();
    } catch { toast.error("Failed to delete"); }
  };

  const handleBulkImport = async (e) => {
    e.preventDefault();
    if (!importFile) return;
    setImporting(true);
    setImportResult(null);
    try {
      const formData = new FormData();
      formData.append("file", importFile);
      const isJson = importFile.name.toLowerCase().endsWith(".json");
      const endpoint = isJson ? "/vendors/import-json" : "/vendors/bulk-import";
      const { data } = await API.post(endpoint, formData, {
        headers: { "Content-Type": "multipart/form-data" }
      });
      setImportResult(data);
      const totalNew = data.imported || 0;
      const totalUpdated = data.updated || 0;
      if (totalNew > 0 || totalUpdated > 0) {
        const parts = [];
        if (totalNew > 0) parts.push(`${totalNew} new`);
        if (totalUpdated > 0) parts.push(`${totalUpdated} updated`);
        toast.success(`Suppliers imported: ${parts.join(", ")}`);
        fetchVendors();
      }
      if (data.errors?.length > 0) {
        toast.warning(`${data.errors.length} rows had errors`);
      }
    } catch (err) { toast.error(err.response?.data?.detail || "Import failed"); }
    finally { setImporting(false); }
  };

  const handleDownloadTemplate = async () => {
    try {
      const response = await API.get("/suppliers/bulk-import/template", { responseType: "blob" });
      const url = URL.createObjectURL(new Blob([response.data]));
      const a = document.createElement("a");
      a.href = url;
      a.download = "vendor_import_template.csv";
      a.click();
      URL.revokeObjectURL(url);
    } catch { toast.error("Failed to download template"); }
  };

  const handleSendReminders = async () => {
    setSendingReminders(true);
    try {
      const { data } = await API.post("/compliance/send-reminders");
      toast.success(data.message);
    } catch { toast.error("Failed to send reminders"); }
    finally { setSendingReminders(false); }
  };

  const [uploadFile, setUploadFile] = useState(null);

  const handleVendorUpload = async (e) => {
    e.preventDefault();
    setUploading(true);
    try {
      const { data: doc } = await API.post("/vendor-portal/upload-document", uploadForm);
      // If a file was selected, attach it to the newly created doc
      if (uploadFile && doc.id) {
        const formData = new FormData();
        formData.append("file", uploadFile);
        await API.post(`/compliance/${doc.id}/attach-file`, formData, { headers: { "Content-Type": "multipart/form-data" } });
      }
      toast.success("Document uploaded for review");
      setShowUploadDoc(false);
      setUploadForm({ doc_type: "insurance", document_number: "", description: "", issue_date: "", expiry_date: "", notes: "" });
      setUploadFile(null);
      fetchMyCompliance();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to upload document");
    }
    finally { setUploading(false); }
  };

  const selectedVendor = vendors.find(v => v.id === selectedVendorId);

  // Version history handlers
  const fetchVersions = async (docId) => {
    setVersionDocId(docId);
    setShowVersionHistory(true);
    setLoadingVersions(true);
    try {
      const { data } = await API.get(`/compliance/${docId}/versions`);
      setVersions(data);
    } catch { setVersions([]); }
    finally { setLoadingVersions(false); }
  };

  const handleUploadNewVersion = async (e) => {
    e.preventDefault();
    setUploadingVersion(true);
    try {
      await API.post(`/compliance/${versionDocId}/new-version`, versionForm);
      toast.success("New version uploaded");
      setShowNewVersion(false);
      setVersionForm({ document_number: "", description: "", issue_date: "", expiry_date: "", notes: "" });
      fetchVersions(versionDocId);
      if (isVendor) fetchMyCompliance();
      else if (selectedVendorId) fetchVendorCompliance(selectedVendorId);
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to upload version");
    }
    finally { setUploadingVersion(false); }
  };

  // File attachment handlers
  const handleAttachFile = async (docId) => {
    if (!fileInput) return;
    setUploadingFile(true);
    try {
      const formData = new FormData();
      formData.append("file", fileInput);
      const { data } = await API.post(`/compliance/${docId}/attach-file`, formData, {
        headers: { "Content-Type": "multipart/form-data" }
      });
      toast.success(`File attached: ${data.file_name}`);
      setFileInput(null);
      if (isVendor) fetchMyCompliance();
      else if (selectedVendorId) fetchVendorCompliance(selectedVendorId);
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to attach file");
    }
    finally { setUploadingFile(false); }
  };

  const fetchDocFiles = async (docId) => {
    setFilesDocId(docId);
    setShowFiles(true);
    try {
      const { data } = await API.get(`/compliance/${docId}/files`);
      setDocFiles(data);
    } catch { setDocFiles([]); }
  };

  // Vendor self-service view
  if (isVendor) {
    return (
      <div data-testid="vendor-portal-page">
        <div className="flex items-center justify-between mb-6">
          <div>
            <h1 className="text-2xl sm:text-3xl font-bold font-[Chivo] tracking-tight">Documents</h1>
            <p className="text-sm text-muted-foreground mt-1">
              {myVendorProfile ? myVendorProfile.company_name : "Compliance Documents"}
            </p>
          </div>
          <Button onClick={() => setShowUploadDoc(true)} data-testid="vendor-upload-doc-btn">
            <FileArrowUp size={16} className="mr-1" /> Upload Document
          </Button>
        </div>

        {myDocsLoading ? (
          <div className="flex justify-center py-12"><div className="animate-spin h-8 w-8 border-2 border-primary border-t-transparent rounded-full" /></div>
        ) : !myVendorProfile ? (
          <div className="text-center py-16 border border-dashed border-border rounded-md">
            <ShieldCheck size={32} className="mx-auto text-muted-foreground mb-2" />
            <p className="text-muted-foreground font-medium mb-1">No supplier profile linked</p>
            <p className="text-xs text-muted-foreground">Your account is not yet linked to a supplier profile. Contact an administrator.</p>
          </div>
        ) : (
          <div>
              {/* Summary cards */}
              <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-6">
                <div className="border border-border rounded-md p-3 text-center">
                  <p className="text-2xl font-bold font-[Chivo] text-foreground">{myDocs.length}</p>
                  <p className="text-[10px] uppercase tracking-widest text-muted-foreground">Total Docs</p>
                </div>
                <div className="border border-emerald-200 dark:border-emerald-800 rounded-md p-3 text-center bg-emerald-50/50 dark:bg-emerald-900/10">
                  <p className="text-2xl font-bold font-[Chivo] text-emerald-600 dark:text-emerald-400">
                    {myDocs.filter(d => d.computed_status === "valid").length}
                  </p>
                  <p className="text-[10px] uppercase tracking-widest text-emerald-700 dark:text-emerald-500">Valid</p>
                </div>
                <div className="border border-amber-200 dark:border-amber-800 rounded-md p-3 text-center bg-amber-50/50 dark:bg-amber-900/10">
                  <p className="text-2xl font-bold font-[Chivo] text-amber-600 dark:text-amber-400">
                    {myDocs.filter(d => d.computed_status === "expiring_soon").length}
                  </p>
                  <p className="text-[10px] uppercase tracking-widest text-amber-700 dark:text-amber-500">Expiring Soon</p>
                </div>
                <div className="border border-red-200 dark:border-red-800 rounded-md p-3 text-center bg-red-50/50 dark:bg-red-900/10">
                  <p className="text-2xl font-bold font-[Chivo] text-red-600 dark:text-red-400">
                    {myDocs.filter(d => d.computed_status === "expired").length}
                  </p>
                  <p className="text-[10px] uppercase tracking-widest text-red-700 dark:text-red-500">Expired</p>
                </div>
              </div>

              {/* Documents table */}
              {myDocs.length === 0 ? (
                <div className="text-center py-16 border border-dashed border-border rounded-md">
                  <ShieldCheck size={32} className="mx-auto text-muted-foreground mb-2" />
                  <p className="text-muted-foreground mb-1">No compliance documents yet</p>
                  <Button variant="outline" size="sm" onClick={() => setShowUploadDoc(true)} data-testid="empty-vendor-upload-btn">Upload Your First Document</Button>
                </div>
              ) : (
                <div className="border border-border rounded-md overflow-hidden">
                  <table className="w-full text-sm" data-testid="my-compliance-docs-table">
                    <thead>
                      <tr className="border-b border-border bg-muted/50 text-xs uppercase tracking-wider text-muted-foreground">
                        <th className="text-left px-4 py-2.5 font-semibold">Document Type</th>
                        <th className="text-left px-4 py-2.5 font-semibold hidden md:table-cell">Number</th>
                        <th className="text-left px-4 py-2.5 font-semibold">Expiry</th>
                        <th className="text-left px-4 py-2.5 font-semibold">Status</th>
                        <th className="text-center px-4 py-2.5 font-semibold">Verified</th>
                        <th className="text-left px-4 py-2.5 font-semibold">Actions</th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-border">
                      {myDocs.map(d => (
                        <tr key={d.id} className="hover:bg-muted/30 transition-colors" data-testid={`my-doc-${d.id}`}>
                          <td className="px-4 py-2.5">
                            <div className="flex items-center gap-1.5">
                              <p className="font-medium capitalize text-xs">{d.doc_type?.replace(/_/g, " ")}</p>
                              {d.current_version > 1 && <Badge variant="outline" className="text-[8px]">v{d.current_version}</Badge>}
                            </div>
                            {d.description && <p className="text-[10px] text-muted-foreground">{d.description}</p>}
                          </td>
                          <td className="px-4 py-2.5 hidden md:table-cell text-xs text-muted-foreground">{d.document_number || "-"}</td>
                          <td className="px-4 py-2.5 text-xs">{fmtDate(d.expiry_date) || "N/A"}</td>
                          <td className="px-4 py-2.5"><ComplianceStatusBadge status={d.computed_status} /></td>
                          <td className="px-4 py-2.5 text-center">
                            {d.verified ? (
                              <span className="text-emerald-600 dark:text-emerald-400 text-[10px] font-medium"><CheckCircle size={16} className="inline" /> Verified</span>
                            ) : (
                              <span className="text-amber-600 dark:text-amber-400 text-[10px] font-medium">Pending Review</span>
                            )}
                          </td>
                          <td className="px-4 py-2.5">
                            <div className="flex gap-1">
                              <Button variant="ghost" size="icon" className="h-6 w-6" onClick={() => { setVersionDocId(d.id); setShowNewVersion(true); }} title="Upload New Version" data-testid={`new-version-${d.id}`}>
                                <FileArrowUp size={14} />
                              </Button>
                              <Button variant="ghost" size="icon" className="h-6 w-6" onClick={() => fetchVersions(d.id)} title="Version History" data-testid={`version-history-${d.id}`}>
                                <ClockCounterClockwise size={14} />
                              </Button>
                              <Button variant="ghost" size="icon" className="h-6 w-6" onClick={() => fetchDocFiles(d.id)} title="Files" data-testid={`files-${d.id}`}>
                                <Upload size={14} />
                              </Button>
                              {d.file_url && (
                                <a href={d.file_url} target="_blank" rel="noreferrer" className="inline-flex items-center justify-center h-6 w-6 hover:bg-muted rounded-md" title="Download File" data-testid={`download-file-${d.id}`}>
                                  <DownloadSimple size={14} />
                                </a>
                              )}
                            </div>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
          </div>
        )}

        {/* Vendor Upload Document Dialog */}
        <Dialog open={showUploadDoc} onOpenChange={setShowUploadDoc}>
          <DialogContent className="max-w-lg" data-testid="vendor-upload-dialog">
            <DialogHeader>
              <DialogTitle className="font-[Chivo]">Upload Compliance Document</DialogTitle>
              <DialogDescription>Submit a compliance document for admin verification.</DialogDescription>
            </DialogHeader>
            <form onSubmit={handleVendorUpload} className="space-y-4">
              <div>
                <Label>Document Type *</Label>
                <Select value={uploadForm.doc_type} onValueChange={v => setUploadForm({...uploadForm, doc_type: v})}>
                  <SelectTrigger data-testid="vendor-doc-type-select"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    {DOC_TYPES.map(t => <SelectItem key={t} value={t}>{t.replace(/_/g, " ").replace(/\b\w/g, c => c.toUpperCase())}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div><Label>Document Number</Label><Input value={uploadForm.document_number} onChange={e => setUploadForm({...uploadForm, document_number: e.target.value})} placeholder="e.g. INS-12345" data-testid="vendor-doc-number-input" /></div>
                <div><Label>Description</Label><Input value={uploadForm.description} onChange={e => setUploadForm({...uploadForm, description: e.target.value})} data-testid="vendor-doc-desc-input" /></div>
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div><Label>Issue Date</Label><DateInput value={uploadForm.issue_date} onChange={e => setUploadForm({...uploadForm, issue_date: e.target.value})} data-testid="vendor-doc-issue-input" /></div>
                <div><Label>Expiry Date</Label><DateInput value={uploadForm.expiry_date} onChange={e => setUploadForm({...uploadForm, expiry_date: e.target.value})} data-testid="vendor-doc-expiry-input" /></div>
              </div>
              <div><Label>Notes</Label><Textarea value={uploadForm.notes} onChange={e => setUploadForm({...uploadForm, notes: e.target.value})} rows={2} placeholder="Any additional notes..." data-testid="vendor-doc-notes-input" /></div>
              <div>
                <Label>Attach Document File</Label>
                <div className="mt-1 border-2 border-dashed border-border rounded-md p-4 text-center hover:border-primary/50 transition-colors">
                  <input
                    type="file"
                    id="vendor-doc-file"
                    className="hidden"
                    accept=".pdf,.jpg,.jpeg,.png,.doc,.docx,.xls,.xlsx"
                    onChange={e => setUploadFile(e.target.files?.[0] || null)}
                    data-testid="vendor-doc-file-input"
                  />
                  <label htmlFor="vendor-doc-file" className="cursor-pointer">
                    {uploadFile ? (
                      <div className="flex items-center justify-center gap-2 text-sm text-primary">
                        <Upload size={16} />
                        <span className="font-medium">{uploadFile.name}</span>
                        <span className="text-[10px] text-muted-foreground">({(uploadFile.size / 1024).toFixed(0)} KB)</span>
                      </div>
                    ) : (
                      <div>
                        <Upload size={24} className="mx-auto text-muted-foreground mb-1" />
                        <p className="text-sm text-muted-foreground">Click to select a file</p>
                        <p className="text-[10px] text-muted-foreground mt-0.5">PDF, Images, Word, Excel</p>
                      </div>
                    )}
                  </label>
                  {uploadFile && (
                    <Button type="button" variant="ghost" size="sm" className="text-[10px] text-destructive mt-1" onClick={() => { setUploadFile(null); document.getElementById("vendor-doc-file").value = ""; }} data-testid="remove-upload-file-btn">
                      Remove file
                    </Button>
                  )}
                </div>
              </div>
              <DialogFooter>
                <Button type="button" variant="outline" onClick={() => setShowUploadDoc(false)}>Cancel</Button>
                <Button type="submit" disabled={uploading} data-testid="submit-vendor-upload-btn">
                  {uploading ? "Uploading..." : "Submit Document"}
                </Button>
              </DialogFooter>
            </form>
          </DialogContent>
        </Dialog>

        {/* Version History + New Version Dialogs (shared) */}
        <Dialog open={showVersionHistory} onOpenChange={setShowVersionHistory}>
          <DialogContent className="max-w-lg" data-testid="version-history-dialog">
            <DialogHeader>
              <DialogTitle className="font-[Chivo]">Version History</DialogTitle>
              <DialogDescription>Previous versions of this compliance document.</DialogDescription>
            </DialogHeader>
            {loadingVersions ? (
              <div className="flex justify-center py-8"><div className="animate-spin h-6 w-6 border-2 border-primary border-t-transparent rounded-full" /></div>
            ) : versions.length === 0 ? (
              <p className="text-sm text-muted-foreground text-center py-4">No version history. This is the original document.</p>
            ) : (
              <div className="space-y-2 max-h-72 overflow-y-auto">
                {versions.map(v => (
                  <div key={v.id} className="border border-border rounded-md p-3 text-xs" data-testid={`version-${v.version}`}>
                    <div className="flex items-center justify-between mb-1">
                      <Badge variant="outline" className="text-[10px]">v{v.version}</Badge>
                      <span className="text-muted-foreground">{new Date(v.created_at).toLocaleDateString()}</span>
                    </div>
                    <p className="text-muted-foreground">Expiry: {fmtDate(v.expiry_date) || "N/A"} | Number: {v.document_number || "-"}</p>
                    <p className="text-muted-foreground mt-0.5">By: {v.uploaded_by}{v.uploaded_by_vendor ? " (vendor)" : ""}</p>
                    {v.notes && <p className="text-muted-foreground mt-0.5 italic">{v.notes}</p>}
                  </div>
                ))}
              </div>
            )}
          </DialogContent>
        </Dialog>

        <Dialog open={showNewVersion} onOpenChange={setShowNewVersion}>
          <DialogContent className="max-w-lg" data-testid="new-version-dialog">
            <DialogHeader>
              <DialogTitle className="font-[Chivo]">Upload New Version</DialogTitle>
              <DialogDescription>Submit an updated version of this document.</DialogDescription>
            </DialogHeader>
            <form onSubmit={handleUploadNewVersion} className="space-y-4">
              <div className="grid grid-cols-2 gap-3">
                <div><Label>Document Number</Label><Input value={versionForm.document_number} onChange={e => setVersionForm({...versionForm, document_number: e.target.value})} data-testid="version-doc-number" /></div>
                <div><Label>Description</Label><Input value={versionForm.description} onChange={e => setVersionForm({...versionForm, description: e.target.value})} data-testid="version-doc-desc" /></div>
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div><Label>Issue Date</Label><DateInput value={versionForm.issue_date} onChange={e => setVersionForm({...versionForm, issue_date: e.target.value})} data-testid="version-issue-date" /></div>
                <div><Label>Expiry Date</Label><DateInput value={versionForm.expiry_date} onChange={e => setVersionForm({...versionForm, expiry_date: e.target.value})} data-testid="version-expiry-date" /></div>
              </div>
              <div><Label>Notes</Label><Textarea value={versionForm.notes} onChange={e => setVersionForm({...versionForm, notes: e.target.value})} rows={2} data-testid="version-notes" /></div>
              <DialogFooter>
                <Button type="button" variant="outline" onClick={() => setShowNewVersion(false)}>Cancel</Button>
                <Button type="submit" disabled={uploadingVersion} data-testid="submit-new-version-btn">
                  {uploadingVersion ? "Uploading..." : "Upload Version"}
                </Button>
              </DialogFooter>
            </form>
          </DialogContent>
        </Dialog>
      </div>
    );
  }

  // Admin view (original)
  return (
    <div data-testid="vendors-page">
      <div className="flex items-center justify-between mb-6">
        <div>
          <h1 className="text-2xl sm:text-3xl font-bold font-[Chivo] tracking-tight">Supplier Directory</h1>
          <p className="text-xs text-muted-foreground mt-0.5">{vendors.length} suppliers</p>
        </div>
        <div className="flex gap-2">
          <Button variant="outline" onClick={() => navigate("/supplier-compare")} data-testid="compare-suppliers-btn">
            Compare
          </Button>
          <Button variant="outline" onClick={() => { setBulkInviteResult(null); setShowBulkInvite(true); }} data-testid="bulk-invite-btn">
            <EnvelopeSimple size={16} className="mr-1" /> Bulk Invite
          </Button>
          <Button variant="outline" onClick={() => { setRefreshInviteResult(null); setShowRefreshInvite(true); }} data-testid="profile-refresh-btn">
            <ArrowsClockwise size={16} className="mr-1" /> Profile Refresh
          </Button>
          <Button variant="outline" onClick={() => setShowBulkImport(true)} data-testid="bulk-import-btn">
            <Upload size={16} className="mr-1" /> Bulk Import
          </Button>
          <Button onClick={() => setShowCreate(true)} data-testid="add-vendor-btn">
            <Plus size={16} className="mr-1" /> Add Supplier
          </Button>
        </div>
      </div>

      <Tabs value={activeTab} onValueChange={setActiveTab}>
        <TabsList data-testid="vendors-tabs">
          <TabsTrigger value="directory" data-testid="vendors-tab-directory">Directory</TabsTrigger>
          <TabsTrigger value="compliance" data-testid="vendors-tab-compliance">
            Compliance
            {complianceSummary && (complianceSummary.expired > 0 || complianceSummary.expiring_soon > 0) && (
              <Badge variant="destructive" className="ml-1.5 h-4 min-w-4 text-[9px] px-1">{complianceSummary.expired + complianceSummary.expiring_soon}</Badge>
            )}
          </TabsTrigger>
          <TabsTrigger value="awards" data-testid="vendors-tab-awards">Awards</TabsTrigger>
          <TabsTrigger value="coverage" data-testid="vendors-tab-coverage">Coverage Map</TabsTrigger>
        </TabsList>

        {/* Directory Tab */}
        <TabsContent value="directory" className="mt-4">
          <div className="flex flex-col sm:flex-row gap-2 mb-4">
            <div className="relative flex-1">
              <MagnifyingGlass size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground" />
              <Input placeholder="Search suppliers... e.g. Plumbers in Plymouth" value={search} onChange={e => setSearch(e.target.value)} className="pl-9" data-testid="vendor-search" />
            </div>
            <Select value={tradeFilter} onValueChange={v => setTradeFilter(v === "all_trades" ? "" : v)}>
              <SelectTrigger className="w-full sm:w-44 text-xs" data-testid="vendor-trade-filter"><SelectValue placeholder="All Services" /></SelectTrigger>
              <SelectContent>
                <SelectItem value="all_trades">All Services</SelectItem>
                {filterOptions.trades.map(t => <SelectItem key={t} value={t}>{t}</SelectItem>)}
              </SelectContent>
            </Select>
            <Select value={areaFilter} onValueChange={v => setAreaFilter(v === "all_areas" ? "" : v)}>
              <SelectTrigger className="w-full sm:w-44 text-xs" data-testid="vendor-area-filter"><SelectValue placeholder="All Areas" /></SelectTrigger>
              <SelectContent className="max-h-72">
                <SelectItem value="all_areas">All Areas</SelectItem>
                <SelectGroup>
                  <SelectLabel>Regions</SelectLabel>
                  {UK_REGIONS.map(r => <SelectItem key={r} value={r}>{r}</SelectItem>)}
                </SelectGroup>
                <SelectGroup>
                  <SelectLabel>Counties</SelectLabel>
                  {ALL_UK_COUNTIES.map(c => <SelectItem key={c} value={c}>{c}</SelectItem>)}
                </SelectGroup>
                {(() => {
                  const std = new Set(ALL_UK_AREAS.map(a => a.toLowerCase()));
                  const extras = filterOptions.areas.filter(a => !std.has(a.toLowerCase()));
                  return extras.length > 0 ? (
                    <SelectGroup>
                      <SelectLabel>Other</SelectLabel>
                      {extras.map(a => <SelectItem key={a} value={a}>{a}</SelectItem>)}
                    </SelectGroup>
                  ) : null;
                })()}
              </SelectContent>
            </Select>
            <Select value={sortBy} onValueChange={setSortBy}>
              <SelectTrigger className="w-full sm:w-48 text-xs" data-testid="vendor-sort-select">
                <SelectValue placeholder="Sort" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="name">Name (A-Z)</SelectItem>
                <SelectItem value="sla">Top performers (SLA)</SelectItem>
                <SelectItem value="response">Fastest response time</SelectItem>
              </SelectContent>
            </Select>
            {(user?.role === "super_admin" || user?.role === "admin") && (
              <>
                <Button
                  variant="outline"
                  size="sm"
                  className="text-xs h-9 px-3"
                  onClick={async () => {
                    if (!window.confirm("This scans every imported supplier and fills in missing search fields (postcode, town, county, sectors). It's safe to run any time. Continue?")) return;
                    try {
                      const { data } = await API.post("/suppliers/reindex");
                      const f = data.filled || {};
                      const total = Object.values(f).reduce((a, b) => a + (b || 0), 0);
                      if (total === 0) {
                        toast.success("All suppliers already indexed");
                      } else {
                        toast.success(
                          `Re-indexed ${data.scanned} suppliers — ${f.postcode || 0} postcodes, ${f.town_city || 0} towns, ${f.county || 0} counties, ${f.sectors_seeded || 0} sectors filled`
                        );
                      }
                      fetchVendors();
                    } catch (e) {
                      toast.error(e.response?.data?.detail || "Re-index failed");
                    }
                  }}
                  data-testid="reindex-suppliers-btn"
                  title="Fill in missing search fields for imported suppliers"
                >
                  <ShieldCheck size={12} className="mr-1" />
                  Re-index
                </Button>
                <Button
                  variant="outline"
                  size="sm"
                  className="text-xs h-9 px-3"
                  onClick={async () => {
                    setRefreshingSla(true);
                    try {
                      const { data } = await API.post("/suppliers/sla/refresh");
                      toast.success(`Refreshed ${data.updated}/${data.scanned} suppliers`);
                      fetchVendors();
                    } catch (e) {
                      toast.error(e.response?.data?.detail || "SLA refresh failed");
                    } finally {
                      setRefreshingSla(false);
                    }
                  }}
                  disabled={refreshingSla}
                  data-testid="refresh-sla-btn"
                  title="Recompute SLA grades now (otherwise auto-runs nightly)"
                >
                  <ShieldCheck size={12} className="mr-1" />
                  {refreshingSla ? "Scoring..." : "Refresh SLA"}
                </Button>
              </>
            )}
            {(search || tradeFilter || areaFilter) && (
              <Button variant="ghost" size="sm" className="text-xs h-9 px-3" onClick={() => { setSearch(""); setTradeFilter(""); setAreaFilter(""); }} data-testid="vendor-clear-filters">
                <ArrowsClockwise size={12} className="mr-1" /> Clear
              </Button>
            )}
          </div>

          {(search || tradeFilter || areaFilter) && (
            <p className="text-xs text-muted-foreground mb-3" data-testid="vendor-results-count">
              {vendors.length} supplier{vendors.length !== 1 ? "s" : ""} found
              {search && <span> matching &ldquo;{search}&rdquo;</span>}
              {tradeFilter && <span> in <strong>{tradeFilter}</strong></span>}
              {areaFilter && <span> covering <strong>{areaFilter}</strong></span>}
            </p>
          )}

          {loading ? (
            <div className="flex justify-center py-12"><div className="animate-spin h-8 w-8 border-2 border-primary border-t-transparent rounded-full" /></div>
          ) : vendors.length === 0 ? (
            <div className="text-center py-16 border border-dashed border-border rounded-md"><p className="text-muted-foreground">No vendors found</p></div>
          ) : (
            <div className="border border-border rounded-md overflow-hidden">
              <table className="w-full text-sm" data-testid="vendors-table">
                <thead>
                  <tr className="border-b border-border bg-muted/50 text-xs uppercase tracking-wider text-muted-foreground">
                    <th className="text-left px-4 py-3 font-semibold">Company</th>
                    <th className="text-left px-4 py-3 font-semibold">Contact</th>
                    <th className="text-left px-4 py-3 font-semibold">Services</th>
                    <th className="text-left px-4 py-3 font-semibold hidden md:table-cell">Areas Covered</th>
                    <th className="text-left px-4 py-3 font-semibold hidden sm:table-cell">SLA</th>
                    <th className="text-left px-4 py-3 font-semibold">Invite</th>
                    <th className="text-left px-4 py-3 font-semibold">Status</th>
                    <th className="text-right px-4 py-3 font-semibold w-28">Actions</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-border">
                  {[...vendors].sort((a, b) => {
                    if (sortBy === "sla") {
                      const sa = typeof a.sla_score === "number" ? a.sla_score : 1;
                      const sb = typeof b.sla_score === "number" ? b.sla_score : 1;
                      if (sa !== sb) return sa - sb;
                      return (a.company_name || "").localeCompare(b.company_name || "");
                    }
                    if (sortBy === "response") {
                      const ax = a.sla_avg_response_seconds ?? Number.MAX_SAFE_INTEGER;
                      const bx = b.sla_avg_response_seconds ?? Number.MAX_SAFE_INTEGER;
                      if (ax !== bx) return ax - bx;
                      return (a.company_name || "").localeCompare(b.company_name || "");
                    }
                    return (a.company_name || "").localeCompare(b.company_name || "");
                  }).map(v => {
                    return (
                      <tr key={v.id} className="row-hover cursor-pointer" data-testid={`vendor-row-${v.id}`} onClick={() => navigate(`/vendors/${v.id}`)}>
                        <td className="px-4 py-3">
                          <div className="flex items-center gap-1.5 flex-wrap">
                            <p className="font-medium text-primary hover:underline">{v.company_name}</p>
                            <SubcontractorBadge supplier={v} compact />
                            {vendorBadges[v.id] && (
                              <SupplierBadgeRibbon badge={vendorBadges[v.id]} compact />
                            )}
                          </div>
                        </td>
                        <td className="px-4 py-3 text-xs text-muted-foreground">{v.contact_name || "-"}</td>
                        <td className="px-4 py-3 text-xs text-muted-foreground capitalize">{v.sectors?.length ? v.sectors.join(", ") : (v.trade || "-")}</td>
                        <td className="px-4 py-3 hidden md:table-cell text-xs text-muted-foreground">{v.areas_covered?.join(", ") || v.coverage_area || "-"}</td>
                        <td className="px-4 py-3 hidden sm:table-cell">
                          <SlaShield vendor={v} />
                        </td>
                        <td className="px-4 py-3">
                          <InviteStatusBadge vendor={v} />
                        </td>
                        <td className="px-4 py-3">
                          <Badge variant={v.status === "active" ? "default" : "secondary"} className="text-[10px]">{v.status?.toUpperCase()}</Badge>
                        </td>
                        <td className="px-4 py-3 text-right">
                          <div className="inline-flex items-center gap-1">
                            {!(v.user_id && v.invite_status === "accepted") && (
                              <Button
                                size="sm"
                                variant="ghost"
                                className="h-7 w-7 p-0"
                                onClick={(e) => { e.stopPropagation(); handleCopySetupLink(v); }}
                                disabled={!v.email}
                                title={!v.email ? "No email on file" : "Copy setup link to clipboard (for SMS, WhatsApp, etc.)"}
                                data-testid={`row-copy-setup-${v.id}`}
                              >
                                <LinkSimple size={13} />
                              </Button>
                            )}
                            <Button
                              size="sm"
                              variant="ghost"
                              className="h-7 px-2 text-[10px]"
                              onClick={(e) => { e.stopPropagation(); handleInviteVendor(v); }}
                              disabled={!v.email}
                              title={!v.email ? "No email on file" : (v.user_id ? "Resend password reset link" : "Send portal invite")}
                              data-testid={`row-invite-${v.id}`}
                            >
                              <EnvelopeSimple size={12} className="mr-1" />
                              {v.user_id ? "Resend" : "Invite"}
                            </Button>
                          </div>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          )}
        </TabsContent>

        {/* Compliance Tab */}
        <TabsContent value="compliance" className="mt-4" data-testid="compliance-tab-content">
          <AutoSuspendToggle />
          {/* Compliance Summary */}
          {complianceSummary && (
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-6">
              <div className="border border-border rounded-md p-3 text-center">
                <p className="text-2xl font-bold font-[Chivo] text-foreground">{complianceSummary.total}</p>
                <p className="text-[10px] uppercase tracking-widest text-muted-foreground">Total Docs</p>
              </div>
              <div className="border border-emerald-200 dark:border-emerald-800 rounded-md p-3 text-center bg-emerald-50/50 dark:bg-emerald-900/10">
                <p className="text-2xl font-bold font-[Chivo] text-emerald-600 dark:text-emerald-400">{complianceSummary.valid}</p>
                <p className="text-[10px] uppercase tracking-widest text-emerald-700 dark:text-emerald-500">Valid</p>
              </div>
              <div className="border border-amber-200 dark:border-amber-800 rounded-md p-3 text-center bg-amber-50/50 dark:bg-amber-900/10">
                <p className="text-2xl font-bold font-[Chivo] text-amber-600 dark:text-amber-400">{complianceSummary.expiring_soon}</p>
                <p className="text-[10px] uppercase tracking-widest text-amber-700 dark:text-amber-500">Expiring Soon</p>
              </div>
              <div className="border border-red-200 dark:border-red-800 rounded-md p-3 text-center bg-red-50/50 dark:bg-red-900/10">
                <p className="text-2xl font-bold font-[Chivo] text-red-600 dark:text-red-400">{complianceSummary.expired}</p>
                <p className="text-[10px] uppercase tracking-widest text-red-700 dark:text-red-500">Expired</p>
              </div>
            </div>
          )}

          {/* Vendor Selector */}
          <div className="flex items-center gap-3 mb-4 flex-wrap">
            <Select value={selectedVendorId || ""} onValueChange={v => fetchVendorCompliance(v)}>
              <SelectTrigger className="w-72" data-testid="compliance-vendor-select">
                <SelectValue placeholder="Select supplier to view compliance..." />
              </SelectTrigger>
              <SelectContent>
                {vendors.map(v => <SelectItem key={v.id} value={v.id}>{v.company_name}</SelectItem>)}
              </SelectContent>
            </Select>
            {selectedVendorId && (
              <Button size="sm" onClick={() => setShowAddDoc(true)} data-testid="add-compliance-doc-btn">
                <Plus size={14} className="mr-1" /> Add Document
              </Button>
            )}
            <Button variant="outline" size="sm" onClick={handleSendReminders} disabled={sendingReminders} data-testid="send-reminders-btn">
              <ArrowsClockwise size={14} className={`mr-1 ${sendingReminders ? "animate-spin" : ""}`} />
              {sendingReminders ? "Sending..." : "Send Reminders"}
            </Button>
          </div>

          {/* Alerts Section */}
          {complianceSummary && !selectedVendorId && (
            <div className="space-y-4">
              {complianceSummary.expired_docs?.length > 0 && (
                <div className="border border-red-200 dark:border-red-800 rounded-md p-4">
                  <h3 className="text-sm font-semibold text-red-700 dark:text-red-400 mb-2 flex items-center gap-1.5"><ShieldWarning size={16} /> Expired Documents</h3>
                  <div className="space-y-2">
                    {complianceSummary.expired_docs.map(d => (
                      <div key={d.id} className="flex items-center justify-between text-xs p-2 bg-red-50 dark:bg-red-900/20 rounded" data-testid={`expired-doc-${d.id}`}>
                        <div>
                          <span className="font-medium capitalize">{d.doc_type?.replace(/_/g, " ")}</span>
                          <span className="text-muted-foreground ml-2">{d.vendor_name}</span>
                        </div>
                        <span className="text-red-600 dark:text-red-400 font-medium">Expired: {fmtDate(d.expiry_date)}</span>
                      </div>
                    ))}
                  </div>
                </div>
              )}
              {complianceSummary.expiring_soon_docs?.length > 0 && (
                <div className="border border-amber-200 dark:border-amber-800 rounded-md p-4">
                  <h3 className="text-sm font-semibold text-amber-700 dark:text-amber-400 mb-2 flex items-center gap-1.5"><ShieldWarning size={16} /> Expiring Soon</h3>
                  <div className="space-y-2">
                    {complianceSummary.expiring_soon_docs.map(d => (
                      <div key={d.id} className="flex items-center justify-between text-xs p-2 bg-amber-50 dark:bg-amber-900/20 rounded" data-testid={`expiring-doc-${d.id}`}>
                        <div>
                          <span className="font-medium capitalize">{d.doc_type?.replace(/_/g, " ")}</span>
                          <span className="text-muted-foreground ml-2">{d.vendor_name}</span>
                        </div>
                        <span className="text-amber-600 dark:text-amber-400 font-medium">Expires: {fmtDate(d.expiry_date)}</span>
                      </div>
                    ))}
                  </div>
                </div>
              )}
              {(!complianceSummary.expired_docs?.length && !complianceSummary.expiring_soon_docs?.length) && (
                <div className="text-center py-12 border border-dashed border-border rounded-md">
                  <ShieldCheck size={32} className="mx-auto text-emerald-500 mb-2" />
                  <p className="text-muted-foreground">All supplier compliance documents are up to date.</p>
                  <p className="text-xs text-muted-foreground mt-1">Select a supplier above to view or add compliance documents.</p>
                </div>
              )}
            </div>
          )}

          {/* Selected Vendor's Documents */}
          {selectedVendorId && (
            <div data-testid="vendor-compliance-docs">
              <div className="flex items-center justify-between mb-3">
                <h3 className="text-sm font-semibold font-[Chivo]">{selectedVendor?.company_name} - Compliance Documents</h3>
                <Button size="sm" onClick={() => setShowAddDoc(true)} data-testid="add-compliance-doc-btn">
                  <Plus size={14} className="mr-1" /> Upload Document
                </Button>
              </div>
              {loadingDocs ? (
                <div className="flex justify-center py-8"><div className="animate-spin h-6 w-6 border-2 border-primary border-t-transparent rounded-full" /></div>
              ) : complianceDocs.length === 0 ? (
                <div className="text-center py-12 border border-dashed border-border rounded-md">
                  <p className="text-muted-foreground mb-1">No compliance documents</p>
                  <Button variant="outline" size="sm" onClick={() => setShowAddDoc(true)} data-testid="empty-add-doc-btn">Add First Document</Button>
                </div>
              ) : (
                <div className="border border-border rounded-md overflow-hidden">
                  <table className="w-full text-sm" data-testid="compliance-docs-table">
                    <thead>
                      <tr className="border-b border-border bg-muted/50 text-xs uppercase tracking-wider text-muted-foreground">
                        <th className="text-left px-4 py-2.5 font-semibold">Document Type</th>
                        <th className="text-left px-4 py-2.5 font-semibold hidden md:table-cell">Number</th>
                        <th className="text-left px-4 py-2.5 font-semibold">Expiry</th>
                        <th className="text-left px-4 py-2.5 font-semibold">Status</th>
                        <th className="text-center px-4 py-2.5 font-semibold hidden md:table-cell">Verified</th>
                        <th className="text-left px-4 py-2.5 font-semibold">Actions</th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-border">
                      {complianceDocs.map(d => (
                        <tr key={d.id} className="hover:bg-muted/30 transition-colors" data-testid={`compliance-doc-${d.id}`}>
                          <td className="px-4 py-2.5">
                            <div className="flex items-center gap-1.5">
                              <p className="font-medium capitalize text-xs">{d.doc_type?.replace(/_/g, " ")}</p>
                              {d.current_version > 1 && <Badge variant="outline" className="text-[8px]">v{d.current_version}</Badge>}
                            </div>
                            {d.description && <p className="text-[10px] text-muted-foreground">{d.description}</p>}
                            {d.uploaded_by_vendor && (
                              <Badge variant="outline" className="text-[8px] mt-0.5">Supplier uploaded</Badge>
                            )}
                          </td>
                          <td className="px-4 py-2.5 hidden md:table-cell text-xs text-muted-foreground">{d.document_number || "-"}</td>
                          <td className="px-4 py-2.5 text-xs">{fmtDate(d.expiry_date) || "N/A"}</td>
                          <td className="px-4 py-2.5"><ComplianceStatusBadge status={d.computed_status} /></td>
                          <td className="px-4 py-2.5 text-center hidden md:table-cell">
                            {d.verified ? (
                              <span className="text-emerald-600 dark:text-emerald-400 text-[10px] font-medium" title={`Verified by ${d.verified_by}`}>Verified</span>
                            ) : <span className="text-muted-foreground text-[10px]">Pending</span>}
                          </td>
                          <td className="px-4 py-2.5">
                            <div className="flex gap-1">
                              {!d.verified && (
                                <Button variant="ghost" size="icon" className="h-6 w-6 text-emerald-600" onClick={() => handleVerifyDoc(d.id)} title="Verify" data-testid={`verify-doc-${d.id}`}>
                                  <CheckCircle size={14} />
                                </Button>
                              )}
                              <Button variant="ghost" size="icon" className="h-6 w-6" onClick={() => { setVersionDocId(d.id); setShowNewVersion(true); }} title="Upload New Version" data-testid={`admin-new-version-${d.id}`}>
                                <FileArrowUp size={14} />
                              </Button>
                              <Button variant="ghost" size="icon" className="h-6 w-6" onClick={() => fetchVersions(d.id)} title="Version History" data-testid={`admin-version-history-${d.id}`}>
                                <ClockCounterClockwise size={14} />
                              </Button>
                              <Button variant="ghost" size="icon" className="h-6 w-6" onClick={() => fetchDocFiles(d.id)} title="Files" data-testid={`admin-files-${d.id}`}>
                                <Upload size={14} />
                              </Button>
                              {d.file_url && (
                                <a href={d.file_url} target="_blank" rel="noreferrer" className="inline-flex items-center justify-center h-6 w-6 hover:bg-muted rounded-md" title="Download File" data-testid={`admin-download-file-${d.id}`}>
                                  <DownloadSimple size={14} />
                                </a>
                              )}
                              <Button variant="ghost" size="icon" className="h-6 w-6 text-destructive" onClick={() => handleDeleteDoc(d.id)} title="Delete" data-testid={`delete-doc-${d.id}`}>
                                <Trash size={14} />
                              </Button>
                            </div>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </div>
          )}
        </TabsContent>

        {/* Awards Tab — Phase 244 Best Supplier Badges */}
        <TabsContent value="awards" className="mt-4" data-testid="awards-tab-content">
          <BestSupplierAwardsPanel onNavigate={(id) => navigate(`/suppliers/${id}`)} />
        </TabsContent>

        <TabsContent value="coverage" className="mt-4" data-testid="coverage-tab-content">
          <SupplierCoverageMap onSelectArea={(area) => { setAreaFilter(area); setActiveTab("directory"); }} />
        </TabsContent>
      </Tabs>

      {/* Version History Dialog (shared for admin) */}
      <Dialog open={showVersionHistory} onOpenChange={setShowVersionHistory}>
        <DialogContent className="max-w-lg" data-testid="admin-version-history-dialog">
          <DialogHeader>
            <DialogTitle className="font-[Chivo]">Version History</DialogTitle>
            <DialogDescription>Previous versions of this compliance document.</DialogDescription>
          </DialogHeader>
          {loadingVersions ? (
            <div className="flex justify-center py-8"><div className="animate-spin h-6 w-6 border-2 border-primary border-t-transparent rounded-full" /></div>
          ) : versions.length === 0 ? (
            <p className="text-sm text-muted-foreground text-center py-4">No version history. This is the original document.</p>
          ) : (
            <div className="space-y-2 max-h-72 overflow-y-auto">
              {versions.map(v => (
                <div key={v.id} className="border border-border rounded-md p-3 text-xs" data-testid={`admin-version-${v.version}`}>
                  <div className="flex items-center justify-between mb-1">
                    <Badge variant="outline" className="text-[10px]">v{v.version}</Badge>
                    <span className="text-muted-foreground">{new Date(v.created_at).toLocaleDateString()}</span>
                  </div>
                  <p className="text-muted-foreground">Expiry: {fmtDate(v.expiry_date) || "N/A"} | Number: {v.document_number || "-"}</p>
                  <p className="text-muted-foreground mt-0.5">By: {v.uploaded_by}{v.uploaded_by_vendor ? " (vendor)" : ""}</p>
                  {v.notes && <p className="text-muted-foreground mt-0.5 italic">{v.notes}</p>}
                </div>
              ))}
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* New Version Dialog (shared for admin) */}
      <Dialog open={showNewVersion} onOpenChange={setShowNewVersion}>
        <DialogContent className="max-w-lg" data-testid="admin-new-version-dialog">
          <DialogHeader>
            <DialogTitle className="font-[Chivo]">Upload New Version</DialogTitle>
            <DialogDescription>Upload an updated version of this compliance document.</DialogDescription>
          </DialogHeader>
          <form onSubmit={handleUploadNewVersion} className="space-y-4">
            <div className="grid grid-cols-2 gap-3">
              <div><Label>Document Number</Label><Input value={versionForm.document_number} onChange={e => setVersionForm({...versionForm, document_number: e.target.value})} data-testid="admin-version-doc-number" /></div>
              <div><Label>Description</Label><Input value={versionForm.description} onChange={e => setVersionForm({...versionForm, description: e.target.value})} data-testid="admin-version-doc-desc" /></div>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div><Label>Issue Date</Label><DateInput value={versionForm.issue_date} onChange={e => setVersionForm({...versionForm, issue_date: e.target.value})} data-testid="admin-version-issue-date" /></div>
              <div><Label>Expiry Date</Label><DateInput value={versionForm.expiry_date} onChange={e => setVersionForm({...versionForm, expiry_date: e.target.value})} data-testid="admin-version-expiry-date" /></div>
            </div>
            <div><Label>Notes</Label><Textarea value={versionForm.notes} onChange={e => setVersionForm({...versionForm, notes: e.target.value})} rows={2} data-testid="admin-version-notes" /></div>
            <DialogFooter>
              <Button type="button" variant="outline" onClick={() => setShowNewVersion(false)}>Cancel</Button>
              <Button type="submit" disabled={uploadingVersion} data-testid="admin-submit-new-version-btn">
                {uploadingVersion ? "Uploading..." : "Upload Version"}
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>

      {/* Create Vendor Dialog */}
      <Dialog open={showCreate} onOpenChange={setShowCreate}>
        <DialogContent className="max-w-lg" data-testid="create-vendor-dialog">
          <DialogHeader><DialogTitle className="font-[Chivo]">Add Supplier</DialogTitle><DialogDescription>Add a new supplier to the directory.</DialogDescription></DialogHeader>
          <form onSubmit={handleCreate} className="space-y-4">
            <div><Label>Company Name *</Label><Input value={form.company_name} onChange={e => setForm({...form, company_name: e.target.value})} required data-testid="vendor-company-input" /></div>
            <div className="grid grid-cols-2 gap-3">
              <div><Label>Contact Name *</Label><Input value={form.contact_name} onChange={e => setForm({...form, contact_name: e.target.value})} required data-testid="vendor-contact-input" /></div>
              <div><Label>Email *</Label><Input type="email" value={form.email} onChange={e => setForm({...form, email: e.target.value})} required data-testid="vendor-email-input" /></div>
            </div>
            <div><Label>Address</Label>
              <AddressAutocomplete
                value={form.address}
                onChange={v => setForm({...form, address: v})}
                onSelect={(a) => setForm(f => ({
                  ...f,
                  address: a.formatted_address || f.address,
                  street_address: a.street_address || "",
                  town_city: a.town_city || "",
                  county: a.county || "",
                  postcode: a.postcode || "",
                  latitude: a.latitude || null,
                  longitude: a.longitude || null,
                }))}
                placeholder="Business address (for distance calculations)"
                data-testid="vendor-address-input"
              />
              {form.latitude && (
                <p className="text-[10px] text-emerald-600 dark:text-emerald-400 mt-1">
                  <CheckCircle size={10} weight="fill" className="inline mr-1" />
                  Location saved — this supplier will appear in "Nearby Suppliers" suggestions
                </p>
              )}
            </div>
            <div><Label>Phone</Label><Input value={form.phone} onChange={e => setForm({...form, phone: e.target.value})} data-testid="vendor-phone-input" /></div>
            <div><Label>Services *</Label>
              <ServicesPicker value={form.sectors || []} onChange={(services) => setForm({ ...form, sectors: services })} testIdPrefix="vendor-services" />
            </div>
            <div><Label>Areas Covered *</Label>
              <UkAreasPicker value={form.areas_covered || []} onChange={(areas) => setForm({ ...form, areas_covered: areas })} testIdPrefix="vendor-areas" allowAdd />
            </div>
            <div><Label>Sectors (comma separated)</Label><Input value={(form.sectors || []).join(", ")} onChange={e => setForm({...form, sectors: e.target.value.split(",").map(s => s.trim()).filter(Boolean)})} placeholder="e.g. Electrical, Plumbing" data-testid="vendor-sectors-input" /></div>
            <div className="grid grid-cols-2 gap-3">
              <div><Label>Company Reg No.</Label><Input value={form.company_registration_number} onChange={e => setForm({...form, company_registration_number: e.target.value})} data-testid="vendor-reg-input" /></div>
              <div><Label>VAT Number</Label><Input value={form.vat_number} onChange={e => setForm({...form, vat_number: e.target.value})} data-testid="vendor-vat-input" /></div>
            </div>
            <div><Label>Website</Label><Input value={form.website} onChange={e => setForm({...form, website: e.target.value})} placeholder="https://..." data-testid="vendor-website-input" /></div>
            <div><Label>Accreditations</Label><Input value={form.accreditations} onChange={e => setForm({...form, accreditations: e.target.value})} placeholder="e.g. Gas Safe, NICEIC" /></div>
            <DialogFooter>
              <Button type="button" variant="outline" onClick={() => setShowCreate(false)}>Cancel</Button>
              <Button type="submit" data-testid="submit-vendor-btn">Add Supplier</Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>

      {/* Edit Vendor Dialog */}
      <Dialog open={showEdit} onOpenChange={setShowEdit}>
        <DialogContent className="max-w-lg" data-testid="edit-vendor-dialog">
          <DialogHeader><DialogTitle className="font-[Chivo]">Edit Supplier</DialogTitle><DialogDescription>Update supplier information.</DialogDescription></DialogHeader>
          {editVendor && (
            <form onSubmit={handleEdit} className="space-y-4">
              <div><Label>Company Name</Label><Input value={editVendor.company_name} onChange={e => setEditVendor({...editVendor, company_name: e.target.value})} /></div>
              <div className="grid grid-cols-2 gap-3">
                <div><Label>Contact Name</Label><Input value={editVendor.contact_name || ""} onChange={e => setEditVendor({...editVendor, contact_name: e.target.value})} /></div>
                <div><Label>Email</Label><Input value={editVendor.email || ""} onChange={e => setEditVendor({...editVendor, email: e.target.value})} /></div>
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div><Label>Phone</Label><Input value={editVendor.phone || ""} onChange={e => setEditVendor({...editVendor, phone: e.target.value})} /></div>
              </div>
              <div><Label>Services</Label>
                <ServicesPicker value={editVendor.sectors?.length ? editVendor.sectors : (editVendor.trade ? editVendor.trade.split(",").map(s => s.trim()).filter(Boolean) : [])} onChange={(services) => setEditVendor({ ...editVendor, sectors: services })} testIdPrefix="vendor-edit-services" />
              </div>
              <div><Label>Areas Covered</Label>
                <UkAreasPicker value={editVendor.areas_covered || []} onChange={(areas) => setEditVendor({ ...editVendor, areas_covered: areas })} testIdPrefix="vendor-edit-areas" allowAdd />
              </div>
              <DialogFooter>
                <Button type="button" variant="outline" onClick={() => setShowEdit(false)}>Cancel</Button>
                <Button type="submit" data-testid="save-vendor-btn">Save Changes</Button>
              </DialogFooter>
            </form>
          )}
        </DialogContent>
      </Dialog>

      {/* Add Compliance Document Dialog */}
      <Dialog open={showAddDoc} onOpenChange={setShowAddDoc}>
        <DialogContent className="max-w-lg" data-testid="add-compliance-dialog">
          <DialogHeader>
            <DialogTitle className="font-[Chivo]">Add Compliance Document</DialogTitle>
            <DialogDescription>Track a compliance document for {selectedVendor?.company_name}.</DialogDescription>
          </DialogHeader>
          <form onSubmit={handleAddComplianceDoc} className="space-y-4">
            <div>
              <Label>Document Type *</Label>
              <Select value={docForm.doc_type} onValueChange={v => setDocForm({...docForm, doc_type: v})}>
                <SelectTrigger data-testid="doc-type-select"><SelectValue /></SelectTrigger>
                <SelectContent>
                  {DOC_TYPES.map(t => <SelectItem key={t} value={t}>{t.replace(/_/g, " ").replace(/\b\w/g, c => c.toUpperCase())}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div><Label>Document Number</Label><Input value={docForm.document_number} onChange={e => setDocForm({...docForm, document_number: e.target.value})} placeholder="e.g. INS-12345" data-testid="doc-number-input" /></div>
              <div><Label>Description</Label><Input value={docForm.description} onChange={e => setDocForm({...docForm, description: e.target.value})} data-testid="doc-desc-input" /></div>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div><Label>Issue Date</Label><DateInput value={docForm.issue_date} onChange={e => setDocForm({...docForm, issue_date: e.target.value})} data-testid="doc-issue-input" /></div>
              <div><Label>Expiry Date</Label><DateInput value={docForm.expiry_date} onChange={e => setDocForm({...docForm, expiry_date: e.target.value})} data-testid="doc-expiry-input" /></div>
            </div>
            <div><Label>Notes</Label><Textarea value={docForm.notes} onChange={e => setDocForm({...docForm, notes: e.target.value})} rows={2} data-testid="doc-notes-input" /></div>
            <div>
              <Label>Attach Document File</Label>
              <div className="mt-1 border-2 border-dashed border-border rounded-md p-4 text-center hover:border-primary/50 transition-colors">
                <input
                  type="file"
                  id="admin-doc-file"
                  className="hidden"
                  accept=".pdf,.jpg,.jpeg,.png,.doc,.docx,.xls,.xlsx"
                  onChange={e => setAdminUploadFile(e.target.files?.[0] || null)}
                  data-testid="admin-doc-file-input"
                />
                <label htmlFor="admin-doc-file" className="cursor-pointer">
                  {adminUploadFile ? (
                    <div className="flex items-center justify-center gap-2 text-sm text-primary">
                      <Upload size={16} />
                      <span className="font-medium">{adminUploadFile.name}</span>
                      <span className="text-[10px] text-muted-foreground">({(adminUploadFile.size / 1024).toFixed(0)} KB)</span>
                    </div>
                  ) : (
                    <div>
                      <Upload size={24} className="mx-auto text-muted-foreground mb-1" />
                      <p className="text-sm text-muted-foreground">Click to select a file</p>
                      <p className="text-[10px] text-muted-foreground mt-0.5">PDF, Images, Word, Excel</p>
                    </div>
                  )}
                </label>
                {adminUploadFile && (
                  <Button type="button" variant="ghost" size="sm" className="text-[10px] text-destructive mt-1" onClick={() => { setAdminUploadFile(null); document.getElementById("admin-doc-file").value = ""; }}>
                    Remove file
                  </Button>
                )}
              </div>
            </div>
            <DialogFooter>
              <Button type="button" variant="outline" onClick={() => setShowAddDoc(false)}>Cancel</Button>
              <Button type="submit" data-testid="submit-compliance-doc-btn">Add Document</Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>


      {/* Profile Refresh Invite Dialog */}
      <Dialog open={showRefreshInvite} onOpenChange={v => { setShowRefreshInvite(v); if (!v) setRefreshInviteResult(null); }}>
        <DialogContent className="max-w-lg" data-testid="profile-refresh-dialog">
          <DialogHeader>
            <DialogTitle className="font-[Chivo]">Invite Suppliers to Update Their Profile</DialogTitle>
            <DialogDescription>
              Emails every active supplier a personal link to the application form, pre-filled with the details you hold. Their submission comes back as a "Profile Update" for you to review and merge — nothing changes on their record until you approve it.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4">
            {refreshProgress && refreshProgress.invited > 0 && (
              <div className="border border-border rounded-lg overflow-hidden" data-testid="refresh-progress-card">
                <div className="px-3 py-2 bg-muted/40 flex items-center justify-between">
                  <span className="text-xs font-semibold">Update Progress</span>
                  <span className="text-[10px] text-muted-foreground">{refreshProgress.completed} of {refreshProgress.invited} completed</span>
                </div>
                <div className="px-3 pt-3">
                  <div className="h-2 rounded-full bg-muted overflow-hidden">
                    <div className="h-full bg-emerald-500 transition-all" style={{ width: `${refreshProgress.invited ? Math.round((refreshProgress.completed / refreshProgress.invited) * 100) : 0}%` }} data-testid="refresh-progress-bar" />
                  </div>
                </div>
                <div className="px-3 py-2.5 flex flex-wrap gap-x-4 gap-y-1 text-[11px]">
                  <span className="text-emerald-600 font-semibold" data-testid="refresh-completed-count">{refreshProgress.completed} completed</span>
                  <span className="text-amber-600 font-semibold" data-testid="refresh-outstanding-count">{refreshProgress.outstanding_count} outstanding</span>
                  {refreshProgress.reminded > 0 && <span className="text-muted-foreground">{refreshProgress.reminded} reminded</span>}
                  {refreshProgress.expired > 0 && <span className="text-red-600">{refreshProgress.expired} link{refreshProgress.expired === 1 ? "" : "s"} expired</span>}
                  {refreshProgress.awaiting_review > 0 && <span className="text-purple-600 font-semibold">{refreshProgress.awaiting_review} awaiting your review</span>}
                  {refreshProgress.merged > 0 && <span className="text-muted-foreground">{refreshProgress.merged} merged</span>}
                </div>
                {refreshProgress.outstanding.length > 0 && (
                  <div className="border-t border-border max-h-36 overflow-y-auto divide-y divide-border/50" data-testid="refresh-outstanding-list">
                    {refreshProgress.outstanding.map(o => (
                      <div key={o.vendor_id} className="px-3 py-1.5 flex items-center gap-2 text-[11px]">
                        <span className="font-medium truncate flex-1">{o.company_name || o.email}</span>
                        {o.reminded && <Badge variant="outline" className="text-[9px] shrink-0">reminded</Badge>}
                        <span className="text-[10px] text-muted-foreground shrink-0">invited {o.invited_at ? new Date(o.invited_at).toLocaleDateString("en-GB", { day: "numeric", month: "short" }) : ""}</span>
                      </div>
                    ))}
                  </div>
                )}
                {(refreshProgress.outstanding_count + refreshProgress.expired) > 0 && (
                  <div className="border-t border-border px-3 py-2">
                    <Button size="sm" variant="outline" className="h-7 w-full text-xs" disabled={refreshInviting}
                            onClick={() => handleRefreshInvite(true)} data-testid="reinvite-outstanding-btn">
                      <EnvelopeSimple size={13} className="mr-1.5" />
                      {refreshInviting ? "Sending..." : `Re-invite Outstanding (${refreshProgress.outstanding_count + refreshProgress.expired})`}
                    </Button>
                    <p className="text-[10px] text-muted-foreground mt-1 text-center">Sends a fresh 30-day link only to suppliers who haven't completed their update (including expired links)</p>
                  </div>
                )}
              </div>
            )}
            <div className="rounded-lg border border-border bg-muted/40 p-3 text-xs space-y-1">
              <p><span className="font-semibold">{vendors.filter(v => v.status === "active" && v.email).length}</span> active suppliers with an email will be invited.</p>
              <p className="text-muted-foreground">Great for long-standing suppliers onboarded before the application form — refreshed trades and areas mean they show up correctly in Area Matches. Links expire in 30 days.</p>
            </div>
            {refreshInviteResult && (
              <div className="border border-border rounded-md p-3 text-sm flex flex-wrap gap-3" data-testid="profile-refresh-result">
                <span className="text-emerald-600 font-semibold">{refreshInviteResult.sent} invited</span>
                <span className="text-muted-foreground">of {refreshInviteResult.total}</span>
                {refreshInviteResult.failed?.length > 0 && <span className="text-red-600">{refreshInviteResult.failed.length} failed</span>}
              </div>
            )}
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowRefreshInvite(false)}>Close</Button>
            {!refreshInviteResult && (
              <Button onClick={() => handleRefreshInvite(false)} disabled={refreshInviting} data-testid="send-profile-refresh-btn">
                {refreshInviting ? "Sending..." : "Send Invites"}
              </Button>
            )}
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Bulk Invite Dialog */}
      <Dialog open={showBulkInvite} onOpenChange={v => { setShowBulkInvite(v); if (!v) setBulkInviteResult(null); }}>
        <DialogContent className="max-w-lg" data-testid="bulk-invite-dialog">
          <DialogHeader>
            <DialogTitle className="font-[Chivo]">Bulk Invite Suppliers</DialogTitle>
            <DialogDescription>
              Send a portal invite email to every supplier with an email on file. Invitations are safe to re-send — each supplier gets a fresh 14-day link.
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-4">
            <div className="rounded-lg border border-border bg-muted/40 p-3 flex items-start gap-2.5">
              <input
                id="only-missing"
                type="checkbox"
                checked={bulkInviteOnlyMissing}
                onChange={e => setBulkInviteOnlyMissing(e.target.checked)}
                className="mt-0.5 h-4 w-4 rounded border-border accent-primary cursor-pointer"
                data-testid="bulk-invite-only-missing"
              />
              <label htmlFor="only-missing" className="text-xs cursor-pointer select-none">
                <span className="font-semibold">Skip suppliers who have already accepted.</span>
                <span className="block text-muted-foreground mt-0.5">
                  Recommended. Uncheck to re-invite <em>every</em> supplier (they will get a password-reset style email).
                </span>
              </label>
            </div>

            {bulkInviteResult && (
              <div className="border border-border rounded-md p-3 space-y-1.5" data-testid="bulk-invite-result">
                {bulkInviteResult.linked_msg ? (
                  <div className="text-xs space-y-1">
                    <p className="font-semibold text-emerald-700 dark:text-emerald-400">{bulkInviteResult.linked_msg}</p>
                    {bulkInviteResult.link_details?.length > 0 && (
                      <div className="max-h-32 overflow-y-auto space-y-0.5 pt-1">
                        {bulkInviteResult.link_details.slice(0, 20).map((d, i) => (
                          <p key={`${d.vendor || ""}-${d.collection || ""}-${i}`} className="text-[11px] text-muted-foreground">
                            <span className="font-medium">{d.vendor}</span>: {d.repointed} {d.collection} repointed
                          </p>
                        ))}
                      </div>
                    )}
                  </div>
                ) : (
                  <>
                    <div className="flex flex-wrap gap-3 text-sm">
                      <span className="text-emerald-600 font-semibold">{bulkInviteResult.sent} sent</span>
                      <span className="text-muted-foreground">of {bulkInviteResult.total_candidates} candidates</span>
                      {bulkInviteResult.skipped_accepted > 0 && <span className="text-sky-600">{bulkInviteResult.skipped_accepted} already active</span>}
                      {bulkInviteResult.skipped_no_email > 0 && <span className="text-amber-600">{bulkInviteResult.skipped_no_email} no email</span>}
                      {bulkInviteResult.failed?.length > 0 && <span className="text-red-600">{bulkInviteResult.failed.length} failed</span>}
                    </div>
                    {bulkInviteResult.failed?.length > 0 && (
                      <div className="max-h-32 overflow-y-auto space-y-1 pt-1.5">
                        {bulkInviteResult.failed.slice(0, 20).map((f, i) => (
                          <p key={`${f.company_name || ""}-${i}`} className="text-[11px] text-red-600">{f.company_name}: {f.error}</p>
                        ))}
                      </div>
                    )}
                  </>
                )}
              </div>
            )}
          </div>

          <DialogFooter className="flex-col sm:flex-row gap-2">
            <Button
              type="button"
              variant="ghost"
              size="sm"
              onClick={handleLinkUsers}
              disabled={bulkInviting}
              className="mr-auto text-xs"
              data-testid="link-users-btn"
              title="Scan tenant for suppliers that share an email with an existing user account and bi-directionally link them. Also heals any legacy job assignments."
            >
              {bulkInviting ? "Working…" : "Link users & heal assignments"}
            </Button>
            <Button type="button" variant="outline" onClick={() => setShowBulkInvite(false)} disabled={bulkInviting}>
              {bulkInviteResult ? "Close" : "Cancel"}
            </Button>
            <Button type="button" onClick={handleBulkInvite} disabled={bulkInviting} data-testid="bulk-invite-send-btn">
              {bulkInviting ? "Sending…" : bulkInviteResult?.sent ? "Send again" : "Send invites"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Bulk Import Dialog */}
      <Dialog open={showBulkImport} onOpenChange={v => { setShowBulkImport(v); if (!v) { setImportFile(null); setImportResult(null); } }}>
        <DialogContent className="max-w-lg" data-testid="bulk-import-dialog">
          <DialogHeader>
            <DialogTitle className="font-[Chivo]">Import Suppliers</DialogTitle>
            <DialogDescription>Upload a CSV or JSON file to import suppliers. JSON supports legacy platform exports.</DialogDescription>
          </DialogHeader>
          <form onSubmit={handleBulkImport} className="space-y-4">
            <div>
              <Button type="button" variant="outline" size="sm" className="mb-3" onClick={handleDownloadTemplate} data-testid="download-template-btn">
                <DownloadSimple size={14} className="mr-1" /> Download CSV Template
              </Button>
              <div className="border-2 border-dashed border-border rounded-md p-6 text-center hover:border-primary/40 transition-colors">
                <Upload size={24} className="mx-auto text-muted-foreground mb-2" />
                <p className="text-sm text-muted-foreground mb-2">
                  {importFile ? importFile.name : "Drop your CSV or JSON file here or click to browse"}
                </p>
                <input
                  type="file"
                  accept=".csv,.json"
                  onChange={e => setImportFile(e.target.files?.[0] || null)}
                  className="block w-full text-sm text-muted-foreground file:mr-4 file:py-1.5 file:px-3 file:rounded-md file:border-0 file:text-xs file:font-medium file:bg-primary file:text-primary-foreground hover:file:bg-primary/90 cursor-pointer"
                  data-testid="import-file-input"
                />
              </div>
            </div>
            <div className="text-xs text-muted-foreground space-y-2">
              <div className="p-2.5 rounded-lg bg-muted/40">
                <p className="font-semibold mb-0.5">JSON (Platform Export)</p>
                <p>Upload the JSON export from your previous supplier platform. All supplier data, documents, and ratings will be imported. Existing suppliers (by company name) will be overwritten.</p>
              </div>
              <div className="p-2.5 rounded-lg bg-muted/40">
                <p className="font-semibold mb-0.5">CSV</p>
                <p>Columns: company_name (required), contact_name, email, phone, trade, coverage_area, accreditations</p>
              </div>
            </div>
            {importResult && (
              <div className="border border-border rounded-md p-3 animate-fade-in" data-testid="import-result">
                <div className="flex flex-wrap gap-3 text-sm mb-2">
                  {(importResult.imported > 0) && (
                    <span className="text-emerald-600 font-medium">{importResult.imported} new</span>
                  )}
                  {(importResult.updated > 0) && (
                    <span className="text-blue-600 font-medium">{importResult.updated} updated</span>
                  )}
                  {(importResult.documents_imported > 0) && (
                    <span className="text-purple-600 font-medium">{importResult.documents_imported} documents</span>
                  )}
                  {(importResult.ratings_imported > 0) && (
                    <span className="text-amber-600 font-medium">{importResult.ratings_imported} ratings</span>
                  )}
                  {importResult.errors?.length > 0 && (
                    <span className="text-red-600 font-medium">{importResult.errors.length} errors</span>
                  )}
                </div>
                {importResult.errors?.length > 0 && (
                  <div className="max-h-32 overflow-y-auto space-y-1">
                    {importResult.errors.map((e, i) => (
                      <p key={`row-${e.row || i}`} className="text-[11px] text-red-600">Row {e.row}: {e.error}</p>
                    ))}
                  </div>
                )}
              </div>
            )}
            <DialogFooter>
              <Button type="button" variant="outline" onClick={() => setShowBulkImport(false)}>Cancel</Button>
              <Button type="submit" disabled={!importFile || importing} data-testid="submit-import-btn">
                {importing ? "Importing..." : "Import Suppliers"}
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>

      {/* Files Dialog with Drag & Drop */}
      <Dialog open={showFiles} onOpenChange={setShowFiles}>
        <DialogContent className="max-w-lg" data-testid="files-dialog">
          <DialogHeader>
            <DialogTitle className="font-[Chivo]">Document Files</DialogTitle>
            <DialogDescription>Drag and drop files or click to browse. Max 10MB per file.</DialogDescription>
          </DialogHeader>
          <div className="space-y-3">
            {/* Drop Zone */}
            <div
              className={`border-2 border-dashed rounded-md p-6 text-center transition-colors cursor-pointer ${dragOver ? "border-primary bg-primary/5" : "border-border hover:border-muted-foreground/50"}`}
              data-testid="file-drop-zone"
              onDragOver={(e) => { e.preventDefault(); setDragOver(true); }}
              onDragLeave={() => setDragOver(false)}
              onDrop={async (e) => {
                e.preventDefault();
                setDragOver(false);
                const files = Array.from(e.dataTransfer.files);
                if (files.length === 0) return;
                setMultiUploading(true);
                setUploadProgress(files.map(f => ({ name: f.name, status: "uploading" })));
                const formData = new FormData();
                files.forEach((f, i) => formData.append(`file_${i}`, f));
                try {
                  const { data } = await API.post(`/compliance/${filesDocId}/attach-files`, formData, {
                    headers: { "Content-Type": "multipart/form-data" }
                  });
                  const results = files.map(f => {
                    const up = (data.uploaded || []).find(u => u.file_name === f.name);
                    const err = (data.errors || []).find(e => e.file === f.name);
                    return { name: f.name, status: up ? "done" : "error", error: err?.error };
                  });
                  setUploadProgress(results);
                  toast.success(`${data.total_uploaded} file(s) uploaded`);
                  fetchDocFiles(filesDocId);
                  if (isVendor) fetchMyCompliance();
                  else if (selectedVendorId) fetchVendorCompliance(selectedVendorId);
                } catch (err) {
                  setUploadProgress(files.map(f => ({ name: f.name, status: "error", error: "Upload failed" })));
                  toast.error("Failed to upload files");
                }
                finally { setMultiUploading(false); }
              }}
              onClick={() => document.getElementById("multi-file-input")?.click()}
            >
              <input
                id="multi-file-input"
                type="file"
                multiple
                className="hidden"
                onChange={async (e) => {
                  const files = Array.from(e.target.files || []);
                  if (files.length === 0) return;
                  setMultiUploading(true);
                  setUploadProgress(files.map(f => ({ name: f.name, status: "uploading" })));
                  const formData = new FormData();
                  files.forEach((f, i) => formData.append(`file_${i}`, f));
                  try {
                    const { data } = await API.post(`/compliance/${filesDocId}/attach-files`, formData, {
                      headers: { "Content-Type": "multipart/form-data" }
                    });
                    const results = files.map(f => {
                      const up = (data.uploaded || []).find(u => u.file_name === f.name);
                      const err = (data.errors || []).find(er => er.file === f.name);
                      return { name: f.name, status: up ? "done" : "error", error: err?.error };
                    });
                    setUploadProgress(results);
                    toast.success(`${data.total_uploaded} file(s) uploaded`);
                    fetchDocFiles(filesDocId);
                    if (isVendor) fetchMyCompliance();
                    else if (selectedVendorId) fetchVendorCompliance(selectedVendorId);
                  } catch {
                    setUploadProgress(files.map(f => ({ name: f.name, status: "error", error: "Upload failed" })));
                    toast.error("Failed to upload files");
                  }
                  finally { setMultiUploading(false); e.target.value = ""; }
                }}
                data-testid="multi-file-input"
              />
              {multiUploading ? (
                <div className="flex items-center justify-center gap-2">
                  <div className="animate-spin h-4 w-4 border-2 border-primary border-t-transparent rounded-full" />
                  <span className="text-xs text-muted-foreground">Uploading...</span>
                </div>
              ) : (
                <div>
                  <Upload size={24} className="mx-auto text-muted-foreground mb-2" />
                  <p className="text-xs text-muted-foreground">
                    {dragOver ? "Drop files here" : "Drag & drop files here, or click to browse"}
                  </p>
                  <p className="text-[10px] text-muted-foreground mt-1">Supports multiple files (max 10MB each)</p>
                </div>
              )}
            </div>

            {/* Upload Progress */}
            {uploadProgress.length > 0 && (
              <div className="space-y-1" data-testid="upload-progress">
                {uploadProgress.map((p, i) => (
                  <div key={`${p.name || ""}-${i}`} className="flex items-center justify-between text-xs py-1 px-2 rounded bg-muted/50">
                    <span className="truncate flex-1 mr-2">{p.name}</span>
                    {p.status === "uploading" && <span className="text-amber-600 text-[10px]">Uploading...</span>}
                    {p.status === "done" && <span className="text-emerald-600 text-[10px]">Done</span>}
                    {p.status === "error" && <span className="text-red-600 text-[10px]" title={p.error}>Failed</span>}
                  </div>
                ))}
              </div>
            )}

            {/* File list */}
            {docFiles.length === 0 ? (
              <p className="text-xs text-muted-foreground text-center py-4">No files attached yet.</p>
            ) : (
              <div className="space-y-2 max-h-64 overflow-y-auto">
                {docFiles.map(f => (
                  <div key={f.id} className="flex items-center justify-between border border-border rounded-md p-2 text-xs" data-testid={`file-${f.id}`}>
                    <div className="min-w-0 flex-1">
                      <p className="font-medium truncate">{f.file_name}</p>
                      <p className="text-[10px] text-muted-foreground">{(f.size_bytes / 1024).toFixed(1)} KB | {f.uploaded_by_name} | {new Date(f.created_at).toLocaleDateString()}</p>
                    </div>
                    {f.file_url && (
                      <a href={f.file_url} target="_blank" rel="noreferrer" className="ml-2 shrink-0" data-testid={`download-attached-${f.id}`}>
                        <Button variant="ghost" size="icon" className="h-7 w-7"><DownloadSimple size={14} /></Button>
                      </a>
                    )}
                  </div>
                ))}
              </div>
            )}
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}

function InviteStatusBadge({ vendor }) {
  const hasEmail = !!vendor.email;
  const hasUser = !!vendor.user_id;
  const status = (vendor.invite_status || "").toLowerCase();

  let label = "—";
  let cls = "bg-slate-100 text-slate-600 dark:bg-slate-800 dark:text-slate-400 border-transparent";

  if (!hasEmail) {
    label = "No email";
    cls = "bg-slate-100 text-slate-500 dark:bg-slate-800 dark:text-slate-500 border-transparent";
  } else if (hasUser || status === "accepted") {
    label = "Active";
    cls = "bg-emerald-100 text-emerald-700 dark:bg-emerald-900/40 dark:text-emerald-400 border-transparent";
  } else if (status === "invited") {
    label = "Invited";
    cls = "bg-sky-100 text-sky-700 dark:bg-sky-900/40 dark:text-sky-400 border-transparent";
  } else {
    label = "Not invited";
    cls = "bg-amber-100 text-amber-700 dark:bg-amber-900/40 dark:text-amber-400 border-transparent";
  }
  return <Badge className={`text-[9px] border ${cls}`} data-testid={`invite-badge-${vendor.id}`}>{label}</Badge>;
}



/** Phase 244 — Best Supplier Awards panel: leaderboard of awarded badges
 * grouped by month with a manual "Run now" trigger for admins. */
function BestSupplierAwardsPanel({ onNavigate }) {
  const [badges, setBadges] = useState([]);
  const [currentPeriod, setCurrentPeriod] = useState(null);
  const [loading, setLoading] = useState(true);
  const [running, setRunning] = useState(false);

  const fetchBadges = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await API.get("/suppliers/badges/leaderboard", { params: { limit: 24 } });
      setBadges(data?.badges || []);
      setCurrentPeriod(data?.current_period || null);
    } catch {
      toast.error("Failed to load badge leaderboard");
    } finally { setLoading(false); }
  }, []);

  useEffect(() => { fetchBadges(); }, [fetchBadges]);

  const runNow = async () => {
    if (running) return;
    if (!window.confirm("Compute & email Best Supplier badges for last month now?")) return;
    setRunning(true);
    try {
      const { data } = await API.post("/scheduler/monthly-badges/run-now", {});
      toast.success(`Awarded ${data?.badges_awarded || 0} badges, sent ${data?.emails_sent || 0} emails (${data?.period_label || ""})`);
      await fetchBadges();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed to run badges");
    } finally { setRunning(false); }
  };

  // Group badges by period_label
  const grouped = badges.reduce((acc, b) => {
    const key = b.period_label || "Unknown";
    (acc[key] = acc[key] || []).push(b);
    return acc;
  }, {});
  const periods = Object.keys(grouped);

  return (
    <div className="space-y-5" data-testid="best-supplier-awards-panel">
      <div className="flex flex-wrap items-center justify-between gap-3 border border-border rounded-lg p-4 bg-card">
        <div className="min-w-0 flex-1">
          <h2 className="text-sm font-semibold font-[Chivo]">Best Supplier of the Month</h2>
          <p className="text-[11px] text-muted-foreground mt-0.5">
            Top-3 suppliers per month, awarded automatically on the 1st. Each receives a branded email + portal badge.
          </p>
          {currentPeriod && (
            <p className="text-[10px] text-muted-foreground mt-1">Most recent period: <span className="font-semibold">{currentPeriod}</span></p>
          )}
        </div>
        <Button onClick={runNow} disabled={running} variant="outline" size="sm" data-testid="run-monthly-badges-btn">
          <ArrowsClockwise size={14} className={`mr-1 ${running ? "animate-spin" : ""}`} />
          {running ? "Running..." : "Run for last month"}
        </Button>
      </div>

      {loading ? (
        <div className="flex justify-center py-12"><div className="animate-spin h-8 w-8 border-2 border-primary border-t-transparent rounded-full" /></div>
      ) : periods.length === 0 ? (
        <div className="border border-dashed border-border rounded-lg p-8 text-center text-sm text-muted-foreground" data-testid="no-awards-empty">
          No badges have been awarded yet. Click <span className="font-semibold">"Run for last month"</span> above to issue the first round.
        </div>
      ) : (
        periods.map(period => (
          <div key={period} data-testid={`awards-period-${period}`}>
            <p className="text-[10px] uppercase tracking-[0.18em] font-bold text-muted-foreground mb-2">{period}</p>
            <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-3">
              {grouped[period].sort((a, b) => a.tier_rank - b.tier_rank).map(b => (
                <div
                  key={b.id}
                  role="button"
                  tabIndex={0}
                  onClick={() => onNavigate?.(b.vendor_id)}
                  onKeyDown={(e) => { if (e.key === "Enter" || e.key === " ") { e.preventDefault(); onNavigate?.(b.vendor_id); } }}
                  className="block cursor-pointer active:scale-[0.98] transition-transform"
                  data-testid={`awards-row-${b.id}`}
                >
                  <SupplierBadgeRibbon badge={b} showShare />
                </div>
              ))}
            </div>
          </div>
        ))
      )}
    </div>
  );
}
